"use client";

import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Send, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { CONTACT_TOPICS, type ContactTopic } from "./contact-topics";


export default function ContactContent({ topic }: { topic?: ContactTopic }) {
  const preset = topic ? CONTACT_TOPICS[topic] : undefined;
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: preset?.subject ?? "",
    body: preset?.body ?? "",
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef<TurnstileInstance>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  // After sending, the form is replaced, so move focus to the confirmation
  // heading — otherwise focus is lost and screen readers announce nothing.
  const sentHeadingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (sent) sentHeadingRef.current?.focus();
  }, [sent]);

  function setField(key: keyof typeof formData, value: string) {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!turnstileToken) {
      setError("Please complete the captcha");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, turnstileToken }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to send message");
      }
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <section className="px-4 py-10 sm:px-6 w-screen">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className={cn(
            "relative mx-auto w-full max-w-xl rounded-2xl border border-white/10 bg-background/40 backdrop-blur-xl",
            "shadow-[0_10px_40px_-10px_rgba(0,0,0,0.6)] ring-1 ring-white/5",
            "p-8 sm:p-12 text-center"
          )}
        >
          <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-green-400" aria-hidden="true" />
          <h2 ref={sentHeadingRef} tabIndex={-1} className="text-xl font-semibold outline-none">
            Message sent!
          </h2>
          <p className="mt-2 text-sm text-foreground/60">
            Thanks for reaching out. I&apos;ll get back to you as soon as I can.
          </p>
          <Button
            className="mt-6"
            variant="outline"
            onClick={() => {
              setSent(false);
              setFormData({ name: "", email: "", subject: "", body: "" });
            }}
          >
            Send another message
          </Button>
        </motion.div>
      </section>
    );
  }

  return (
    <section className="px-4 py-10 sm:px-6 w-screen">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className={cn(
          "relative mx-auto w-full max-w-xl rounded-2xl border border-white/10 bg-background/40 backdrop-blur-xl",
          "shadow-[0_10px_40px_-10px_rgba(0,0,0,0.6)] ring-1 ring-white/5",
          "p-5 sm:p-8",
          "before:pointer-events-none before:absolute before:inset-0 before:rounded-2xl before:bg-gradient-to-b before:from-white/5 before:to-transparent"
        )}
      >
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          {preset?.heading ?? "Get in touch"}
        </h1>
        <p className="mt-2 text-sm text-foreground/60">
          {preset?.intro ??
            "Have a question or want to work together? Send me a message and I'll get back to you."}
        </p>

        {/* toolname/tooldescription expose the form to in-browser agents via
            WebMCP; the person still completes the captcha and submits. */}
        <form
          onSubmit={handleSubmit}
          className="mt-6 space-y-5"
          toolname="contact_josh"
          tooldescription="Fill in a message to Josh Wood. The visitor must complete the captcha and press Send themselves."
        >
          {error && (
            <div role="alert" className="rounded-md bg-red-500/10 p-3 text-sm text-red-500">
              {error}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                name="name"
                toolparamdescription="Sender's name"
                value={formData.name}
                onChange={(e) => setField("name", e.target.value)}
                required
                placeholder="Your name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                toolparamdescription="Sender's email address, for Josh's reply"
                type="email"
                value={formData.email}
                onChange={(e) => setField("email", e.target.value)}
                required
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              name="subject"
              toolparamdescription="Short subject line"
              value={formData.subject}
              onChange={(e) => setField("subject", e.target.value)}
              placeholder="What's this about?"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="body">Message</Label>
            <Textarea
              id="body"
              name="body"
              toolparamdescription="The message to Josh"
              value={formData.body}
              onChange={(e) => setField("body", e.target.value)}
              required
              rows={6}
              placeholder="Your message..."
            />
          </div>

          {process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && (
            <Turnstile
              ref={turnstileRef}
              siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
              onSuccess={setTurnstileToken}
              onExpire={() => setTurnstileToken("")}
              options={{ theme: "dark" }}
            />
          )}

          <Button type="submit" disabled={loading || !turnstileToken} className="w-full sm:w-auto">
            {loading ? (
              "Sending..."
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Send Message
              </>
            )}
          </Button>
        </form>
      </motion.div>
    </section>
  );
}
